/**
 * Client wrapper for the resendRfqPackageScopeChange callable — staff-only
 * "Resend — scope changed" action. Updates the already-published package's
 * structure scope in place (packages are client-immutable) and emails the
 * supplier a same-file scope-update notice.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { CncSpeciesAssignment, ProposalNodeSet, TimberSupplierStructureRef } from '../types';

export type ResendRfqPackageScopeChangeInput = {
  packageId: string;
  /** Structure refs of the supplier's new scope; empty = all structures. */
  scopeStructures?: TimberSupplierStructureRef[];
  /** Timber only — re-scoped node sets for the portal download and email CSV. */
  proposalNodeSets?: ProposalNodeSet[];
  /** CNC only — replacement species assignments for the updated scope. */
  cncSpeciesAssignments?: CncSpeciesAssignment[];
  /** CNC only — updated board feet total for the new scope. */
  cncBoardFeet?: number;
  /** Structure names for the email banner. */
  addedStructureNames?: string[];
  removedStructureNames?: string[];
  /**
   * Timber only — species per structure (structure id → species) snapshotted onto
   * the package; null clears it (every species everywhere). Absent = unchanged.
   */
  structureSpecies?: Record<string, string[]> | null;
  /** Per-structure species change lines for the email banner. */
  speciesChangeLines?: string[];
  customMessage?: string;
};

export type ResendRfqPackageScopeChangeResult = {
  ok: boolean;
  recipients: number;
  simulated: boolean;
};

export async function resendRfqPackageScopeChange(
  input: ResendRfqPackageScopeChangeInput,
): Promise<ResendRfqPackageScopeChangeResult> {
  const functions = getFunctions(undefined, 'us-central1');
  const callable = httpsCallable<ResendRfqPackageScopeChangeInput, ResendRfqPackageScopeChangeResult>(
    functions,
    'resendRfqPackageScopeChange',
  );
  const result = await callable(input);
  return result.data;
}
